/**
 * `next/dynamic` compatibility shim.
 *
 * Maps Next's `dynamic(() => import('./Foo'), { ssr: false, loading: ... })`
 * onto `React.lazy` + `Suspense`. The `ssr` flag is irrelevant in the Vite
 * SPA build and is silently dropped; the `loading` component, if provided,
 * is used as the Suspense fallback.
 *
 * Next's dynamic supports named exports via a chained `.then((mod) => mod.X)`;
 * that pattern lives in user code and works under React.lazy unchanged.
 */
import { lazy, Suspense } from 'react';
import type { ComponentType, ReactNode } from 'react';

interface DynamicOptions<P> {
  ssr?: boolean;
  loading?: ComponentType | (() => ReactNode);
  suspense?: boolean;
  // Match Next's signature surface area without using it.
  _propsForwarder?: P;
}

type Loader<P> = () => Promise<{ default: ComponentType<P> } | ComponentType<P>>;

export default function dynamic<P = Record<string, unknown>>(
  loader: Loader<P>,
  options: DynamicOptions<P> = {}
): ComponentType<P> {
  // Normalize: React.lazy requires a default export. If the loader resolves
  // to a bare component (the .then((m) => m.Named) pattern), wrap it.
  const normalized = () =>
    loader().then((mod) => {
      if (mod && typeof mod === 'object' && 'default' in mod) {
        return mod as { default: ComponentType<P> };
      }
      return { default: mod as ComponentType<P> };
    });

  const LazyComponent = lazy(normalized);
  const LoadingComponent = options.loading;

  return function DynamicComponent(props: P) {
    const fallback = LoadingComponent ? <LoadingComponent /> : null;
    return (
      <Suspense fallback={fallback}>
        {/* @ts-expect-error - lazy component props are inferred from loader */}
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}
